/**
 * Custom hook for JSONCargo tracking API statistics
 */
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { apiGet } from "@/lib/api-client";
import type { JSONCargoStatsResponse } from "@/types/api/tracking";

const TRACKING_QUERY_KEYS = {
    all: ["tracking"] as const,
    stats: () => [...TRACKING_QUERY_KEYS.all, "stats"] as const,
};

/**
 * Hook to fetch JSONCargo API usage statistics
 *
 * Returns:
 * - plan: Plan name (e.g., "MARINER")
 * - requests_total: Total API calls in plan
 * - requests_made: API calls used
 * - requests_available: Remaining API calls
 */
export function useTrackingStats() {
    return useQuery({
        queryKey: TRACKING_QUERY_KEYS.stats(),
        queryFn: async (): Promise<JSONCargoStatsResponse> => {
            return await apiGet<JSONCargoStatsResponse>("/tracking/stats");
        },
        staleTime: 60 * 1000, // 1 minute
        refetchInterval: 5 * 60 * 1000, // Refresh every 5 minutes
    });
}

/**
 * Hook to manually refetch tracking stats
 */
export function useRefetchTrackingStats() {
    const queryClient = useQueryClient();

    return () => {
        queryClient.invalidateQueries({ queryKey: TRACKING_QUERY_KEYS.stats() });
    };
}
